import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components/macro';

import { lazyWithPreload } from '../../utilities/lazy-with-preload';
import { useAppContext, useFrami, useNavigation, useWorkspace } from '../../context';
import { CHECKOUT, ORDER_STATUS, WALLBACKGROUND_DRAWER } from '../../providers/NavigationProvider';
import { contentPaddingSides, elevationShadow, smallElevationShadow } from '../../common/styles';
import Dropzone from './Dropzone';
import NavbarWrapper from '../common/navigation/NavbarWrapper';
import WallBackgroundDropzone from './WallBackgroundDropzone';

const Drawer = lazyWithPreload(() => import(/* webpackChunkName: 'Drawer'  */ './Drawer'));
const FloatingBottom = lazyWithPreload(() => import(/* webpackChunkName: 'FloatingBottom'  */ './FloatingBottom'));
const DesktopWorkspaceTools = lazyWithPreload(
    () => import(/* webpackChunkName: 'DesktopWorkspaceTools'  */ '../../components/studio/DesktopWorkspaceTools')
);

const Wrapper = styled.section`
    background-color: white;
    width: ${(props) => props.$isSmall ? '300px' : '384px'};
    position: absolute;
    top: 0px;
    right: 0px;
    /* Changed box-shadow to display shadow only on the left side */
    box-shadow: -5px 0 10px rgba(0, 0, 0, 0.02);
    transition: box-shadow 0.3s;
    cursor: auto;
    height: ${(props) => props.$fullHeight ? '98vh' : '97vh'};
    // margin-right: 12px;
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const Inside = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const RightPanelContent = styled.section`
    display: flex;
    flex-direction: column;
    margin-top: ${(props) => props.$navbarHeight}px;
    background: white;
    padding-left: ${contentPaddingSides};
    padding-right: ${contentPaddingSides};
    max-height: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    margin-bottom: 56px;
`;


const RightPanel = ({ compositionModelLoaded, children }) => {
    const { smallHeightDevice } = useAppContext();
    const { currentFrami } = useFrami();
    const { activeDrawer, setRightPanelWidth, navbarHeight } = useNavigation();
    const { setIsExportingGLTF } = useWorkspace();

    const wrapperRef = useRef(null);

    const [showShadowBox, setShowShadowBox] = useState(false);

    useEffect(() => {
        FloatingBottom.preload();
        DesktopWorkspaceTools.preload();
    }, []);

    useEffect(() => {
        if ((activeDrawer === CHECKOUT || activeDrawer === ORDER_STATUS) && !currentFrami?.is_ordered) {
            setShowShadowBox(true);
        } else {
            setShowShadowBox(false);
        }
    }, [activeDrawer]);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setRightPanelWidth(entry.contentRect.width);
            }
        });

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [setRightPanelWidth]);

    return (
        <Wrapper
            ref={wrapperRef}
            $showShadowBox={showShadowBox}
            $isSmall={smallHeightDevice}
            $fullHeight={activeDrawer === CHECKOUT}
        >
            <Inside>
                {activeDrawer === WALLBACKGROUND_DRAWER ? (
                    <WallBackgroundDropzone
                        noClick={true}
                        style={{display: 'flex', flexDirection: 'column', height: '100%'}}
                    >
                        <NavbarWrapper />
                        <RightPanelContent $navbarHeight={navbarHeight}>
                            <DesktopWorkspaceTools compositionModelLoaded={compositionModelLoaded} />
                            {children}
                        </RightPanelContent>
                        <Drawer />
                        <FloatingBottom />
                    </WallBackgroundDropzone>
                ) : (
                    <Dropzone noClick={true} style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                        <NavbarWrapper />
                        <RightPanelContent $navbarHeight={navbarHeight}>
                            <DesktopWorkspaceTools compositionModelLoaded={compositionModelLoaded} />
                            {children}
                        </RightPanelContent>
                        <Drawer />
                        <FloatingBottom />
                    </Dropzone>
                )}
            </Inside>
        </Wrapper>
    );
};

export default RightPanel;
